import React from 'react';

const PrivacyPolicy = ({ appType }) => {
  const appNames = {
    admin: 'Najum Mamoon Admin Dashboard',
    partner: 'Najum Mamoon Partner App',
    employee: 'Najum Mamoon Employee App'
  };

  const appName = appNames[appType] || 'Najum Mamoon';

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
        <p className="text-sm text-gray-600 mb-8">Effective Date: December 8, 2025</p>

        <div className="space-y-6 text-gray-700">
          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Introduction</h2>
            <p>
              Welcome to {appName}. This Privacy Policy explains how we collect, use, disclose, 
              and safeguard your information when you use our application. Please read this privacy 
              policy carefully. If you do not agree with the terms of this privacy policy, please 
              do not access the application.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Information We Collect</h2>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">Personal Information</h3>
            <p className="mb-2">We may collect personal information that you provide to us, including:</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Name and contact information (email address, phone number)</li>
              <li>Account credentials (username and password)</li>
              <li>Profile information</li>
              <li>Employment details and organizational information</li>
              {appType === 'employee' && <li>Task and work-related data</li>}
              {appType === 'partner' && <li>Business and partnership information</li>}
              {appType === 'admin' && <li>Administrative and management data</li>}
            </ul>

            <h3 className="text-xl font-semibold text-gray-800 mt-4 mb-2">Usage Data</h3>
            <p>We automatically collect certain information when you use our app, including:</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Device information (model, operating system, unique device identifiers)</li>
              <li>Log data (IP address, access times, app features used)</li>
              <li>Performance and diagnostic data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">How We Use Your Information</h2>
            <p className="mb-2">We use the collected information for various purposes:</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>To provide, operate, and maintain our application</li>
              <li>To authenticate users and manage accounts</li>
              <li>To process and manage tasks and workflows</li>
              <li>To communicate with you about updates, security alerts, and support</li>
              <li>To improve our application and user experience</li>
              <li>To monitor and analyze usage patterns</li>
              <li>To detect, prevent, and address technical issues and security threats</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Data Sharing and Disclosure</h2>
            <p className="mb-2">We may share your information in the following situations:</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li><strong>Within Organization:</strong> With other users within your organization as necessary for collaboration and task management</li>
              <li><strong>Service Providers:</strong> With third-party service providers who perform services on our behalf</li>
              <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
              <li><strong>Business Transfers:</strong> In connection with any merger, sale, or acquisition of our business</li>
            </ul>
            <p className="mt-2">We do not sell your personal information to third parties.</p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Data Security</h2>
            <p>
              We implement appropriate technical and organizational security measures to protect your 
              personal information against unauthorized access, alteration, disclosure, or destruction. 
              However, no method of transmission over the internet or electronic storage is 100% secure, 
              and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Data Retention</h2>
            <p>
              We retain your personal information for as long as necessary to fulfill the purposes 
              outlined in this Privacy Policy, unless a longer retention period is required or 
              permitted by law. When we no longer need your information, we will securely delete 
              or anonymize it.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Your Rights</h2>
            <p className="mb-2">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Access, update, or delete your personal information</li>
              <li>Object to or restrict certain processing of your data</li>
              <li>Data portability</li>
              <li>Withdraw consent where processing is based on consent</li>
              <li>Lodge a complaint with a supervisory authority</li>
            </ul>
            <p className="mt-2">
              To exercise these rights, please contact us using the contact information provided below.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Children's Privacy</h2>
            <p>
              Our application is not intended for use by children under the age of 13 (or the 
              applicable age in your jurisdiction). We do not knowingly collect personal information 
              from children. If we become aware that we have collected personal information from a 
              child without parental consent, we will take steps to delete that information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Third-Party Links</h2>
            <p>
              Our application may contain links to third-party websites or services. We are not 
              responsible for the privacy practices of these third parties. We encourage you to 
              review their privacy policies before providing any personal information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the "Effective Date" at the 
              top. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">Contact Us</h2>
            <p className="mb-2">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <ul className="list-none ml-4 space-y-1">
              <li><strong>Email:</strong> info@nujumalrabie.com</li>
              <li><strong>Address:</strong> 150 Sheikh Zayed Rd - Al Quoz 1 - Dubai - United Arab Emirates</li>
              <li><strong>Phone:</strong> +971 55 711 3252</li>
            </ul>
          </section>

          <section className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              By using {appName}, you acknowledge that you have read and understood this Privacy 
              Policy and agree to its terms.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
